class FrameSequenceHero extends HTMLElement {
  /* Preload window tuning (Level 1 optimization).
     PRELOAD_COUNT 50 = eager-load first 50 frames once JS runs (was 30).
     LOOKAHEAD 36 = buffer 36 frames AHEAD of current scroll position (was 24).
     Bigger windows reduce "frame not loaded" misses during fast scroll.
     Bandwidth trade-off: ~3MB upfront vs ~1.8MB before — acceptable for
     hero-priority asset where first impression matters most. */
  static PRELOAD_COUNT = 50;
  static LOOKAHEAD = 36;
  static LOOKBEHIND = 8;
  static FETCH_PRIORITY_HIGH_THRESHOLD = 8; // first N frames get fetchPriority=high

  #canvas;
  #ctx;
  #frames = [];
  #frameUrls = [];
  #frameCount = 0;
  #currentFrame = 0;
  #lastDrawnFrame = -1;
  #dpr = 1;
  #scrollTrigger = null;
  #resizeBound = null;
  #headerGuard = null;
  #header = null;

  connectedCallback() {
    this.#canvas = this.querySelector('[data-canvas]');
    if (!this.#canvas) return;

    this.#ctx = this.#canvas.getContext('2d', { alpha: true, desynchronized: true });
    this.#canvas.style.transform = 'translateZ(0)';

    const framesScript = this.querySelector('script[data-frames]');
    if (!framesScript) return;
    try {
      this.#frameUrls = JSON.parse(framesScript.textContent);
    } catch (e) {
      console.warn('[frame-sequence-hero] failed to parse frame URLs', e);
      return;
    }

    this.#frameCount = this.#frameUrls.length;
    if (this.#frameCount === 0) return;

    this.#frames = new Array(this.#frameCount);
    this.#header = document.querySelector('header-component');

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#resizeBound = () => this.#resizeCanvas();
    this.#resizeCanvas();
    window.addEventListener('resize', this.#resizeBound, { passive: true });

    const firstFrame = this.#loadFrame(0);
    if (firstFrame) {
      firstFrame.addEventListener('load', () => {
        this.dataset.loaded = 'true';
        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      }, { once: true });
    }

    for (let i = 0; i < Math.min(FrameSequenceHero.PRELOAD_COUNT, this.#frameCount); i++) {
      this.#loadFrame(i);
    }
    this.#renderFrame(0);

    if (reducedMotion) {
      this.dataset.reducedMotion = 'true';
      return;
    }

    this.#waitForGsap();
  }

  disconnectedCallback() {
    if (this.#resizeBound) window.removeEventListener('resize', this.#resizeBound);
    this.#scrollTrigger?.kill();
    this.#scrollTrigger = null;
    this.#headerGuard?.disconnect();
    this.#headerGuard = null;
    this.#releaseHeaderTransparent();
    this.#frames = [];
  }

  #loadFrame(index) {
    if (index < 0 || index >= this.#frameCount) return null;
    if (this.#frames[index]) return this.#frames[index];
    const img = new Image();
    img.decoding = 'async';
    /* High fetch priority for first N frames — browser puts these at the
       front of the network queue, ahead of fonts/analytics/etc.
       The Liquid preload tags also hint <link rel="preload" fetchpriority="high">,
       so by the time this runs they're often already cached. The runtime
       hint here is a backup for JS-only loads of frames 5-8. */
    if (index < FrameSequenceHero.FETCH_PRIORITY_HIGH_THRESHOLD) {
      img.fetchPriority = 'high';
    }
    img.src = this.#frameUrls[index];
    this.#frames[index] = img;
    /* Force decode after load so drawImage() during scroll never blocks on
       main-thread decode. WebP decode-on-paint was the #1 lag source.
       img.decoding="async" only opts INTO async decode, doesn't pre-trigger.
       img.decode() forces decode now; after it resolves, every subsequent
       drawImage(img) is a cache hit. */
    img.addEventListener('load', () => {
      if (typeof img.decode === 'function') {
        img.decode().catch(() => {});
      }
    }, { once: true });
    return img;
  }

  #isReady(img) {
    return img && img.complete && img.naturalWidth > 0;
  }

  #paint(img) {
    const cw = this.#canvas.width;
    const ch = this.#canvas.height;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const scale = Math.max(cw / iw, ch / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = (cw - dw) / 2;
    const dy = (ch - dh) / 2;
    this.#ctx.clearRect(0, 0, cw, ch);
    this.#ctx.drawImage(img, dx, dy, dw, dh);
  }

  #resizeCanvas() {
    /* DPR cap: 2x desktop, 1.5x mobile. Mobile GPUs (especially older Android)
       struggle with 2x DPR canvas paint at 60fps when redrawing every frame.
       Visual difference at 1.5x on mobile is negligible for a 1280×720 source. */
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const maxDpr = isMobile ? 1.5 : 2;
    this.#dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
    const rect = this.#canvas.getBoundingClientRect();
    this.#canvas.width = Math.max(1, Math.round(rect.width * this.#dpr));
    this.#canvas.height = Math.max(1, Math.round(rect.height * this.#dpr));
    const img = this.#frames[this.#currentFrame];
    if (this.#isReady(img)) {
      this.#paint(img);
      this.#lastDrawnFrame = this.#currentFrame;
    }
  }

  #renderFrame(index) {
    const target = this.#loadFrame(index);
    if (this.#isReady(target)) {
      if (this.#lastDrawnFrame !== index) {
        this.#paint(target);
        this.#lastDrawnFrame = index;
      }
      return;
    }
    const span = FrameSequenceHero.LOOKBEHIND;
    for (let offset = 1; offset <= span; offset++) {
      const back = this.#frames[index - offset];
      if (this.#isReady(back)) {
        if (this.#lastDrawnFrame !== index - offset) {
          this.#paint(back);
          this.#lastDrawnFrame = index - offset;
        }
        break;
      }
      const fwd = this.#frames[index + offset];
      if (this.#isReady(fwd)) {
        if (this.#lastDrawnFrame !== index + offset) {
          this.#paint(fwd);
          this.#lastDrawnFrame = index + offset;
        }
        break;
      }
    }
    if (target) {
      target.addEventListener('load', () => {
        if (index === this.#currentFrame) {
          this.#paint(target);
          this.#lastDrawnFrame = index;
        }
      }, { once: true });
    }
  }

  #preloadAround(index) {
    const start = Math.max(0, index - FrameSequenceHero.LOOKBEHIND);
    const end = Math.min(this.#frameCount, index + FrameSequenceHero.LOOKAHEAD);
    for (let i = start; i < end; i++) this.#loadFrame(i);
  }

  /**
   * While the user is inside the hero scroll range we want Horizon's header
   * to stay in its transparent state. Horizon's header.js sets
   * data-sticky-state="active" via IntersectionObserver as soon as the
   * page scrolls 1px (because the header-section sticks at top:-1px).
   * We override that by forcing the attribute back to "inactive" whenever
   * something flips it during the pinned range.
   */
  #holdHeaderTransparent() {
    if (!this.#header) return;
    document.body.dataset.heroFrameSequencePinned = 'true';
    if (this.#header.dataset.stickyState === 'active') {
      this.#header.dataset.stickyState = 'inactive';
    }
    if (!this.#headerGuard) {
      this.#headerGuard = new MutationObserver(() => {
        if (
          document.body.dataset.heroFrameSequencePinned === 'true' &&
          this.#header.dataset.stickyState === 'active'
        ) {
          this.#header.dataset.stickyState = 'inactive';
        }
      });
      this.#headerGuard.observe(this.#header, {
        attributes: true,
        attributeFilter: ['data-sticky-state'],
      });
    }
  }

  #releaseHeaderTransparent() {
    if (!this.#header) return;
    delete document.body.dataset.heroFrameSequencePinned;
    this.#headerGuard?.disconnect();
    this.#headerGuard = null;
    /* Explicitly force sticky-state='active' when releasing past hero.
       Why: Horizon's IO only fires on intersection CHANGE. By the time
       MO releases, the user has scrolled past the IO threshold long ago
       (no fresh fire). The MO has been forcing state to "inactive" the
       whole time. Releasing without setting to "active" leaves it stuck
       at "inactive" → no auto-trigger of Horizon's beautiful active-state
       effect (logo swap, underlay reveal, color cascade). */
    this.#header.dataset.stickyState = 'active';
  }

  #waitForGsap() {
    if (window.gsap && window.ScrollTrigger) {
      this.#initScrollTrigger();
      return;
    }
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.gsap && window.ScrollTrigger) {
        clearInterval(interval);
        this.#initScrollTrigger();
      } else if (attempts > 100) {
        clearInterval(interval);
        console.warn('[frame-sequence-hero] GSAP/ScrollTrigger did not load within 10s');
      }
    }, 100);
  }

  #initScrollTrigger() {
    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);

    this.#scrollTrigger = ScrollTrigger.create({
      trigger: this,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.3,
      invalidateOnRefresh: true,
      onEnter: () => this.#holdHeaderTransparent(),
      onEnterBack: () => this.#holdHeaderTransparent(),
      onLeave: () => this.#releaseHeaderTransparent(),
      onLeaveBack: () => this.#releaseHeaderTransparent(),
      onUpdate: (self) => {
        const target = Math.min(
          this.#frameCount - 1,
          Math.floor(self.progress * this.#frameCount)
        );
        if (target !== this.#currentFrame) {
          this.#currentFrame = target;
          this.#renderFrame(target);
          this.#preloadAround(target);
        }
      },
    });

    if (this.#scrollTrigger.isActive) {
      this.#holdHeaderTransparent();
    }

    window.addEventListener('load', () => {
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }, { once: true });
  }
}

if (!customElements.get('frame-sequence-hero')) {
  customElements.define('frame-sequence-hero', FrameSequenceHero);
}
